import CopyOutlined from "@ant-design/icons/CopyOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import HistoryOutlined from "@ant-design/icons/HistoryOutlined";
import PauseCircleOutlined from "@ant-design/icons/PauseCircleOutlined";
import PlayCircleOutlined from "@ant-design/icons/PlayCircleOutlined";
import ShareAltOutlined from "@ant-design/icons/ShareAltOutlined";
import SwapOutlined from "@ant-design/icons/SwapOutlined";
import ThunderboltOutlined from "@ant-design/icons/ThunderboltOutlined";
import React, { useContext, useState } from "react";
import {
  copyCodeToClipboard,
  displayResultMessage,
  openMessage,
} from "utils/misc";
import {
  getDuplicateBotRoute,
  getEditBotRoute,
  getPublicBotPath,
  getTradeExtraDetailsRoute,
} from "utils/routes";
import { useHistory } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { CLOSED_TRADES_PATH, OPEN_TRADES_PATH } from "common/constants";
import {
  BasicBotData,
  BotData,
  BotType,
  FilterType,
  LanguageNameSpaces,
  WorkspaceType,
} from "@shared/types";
import {
  getBotDeleteFailedMessage,
  getBotDeleteWarningMessage,
  getBotDeletedMessage,
} from "@shared/features/bots/utils";
import BulkUpdateBotsModal from "features/bots/MyBots/BulkUpdateBotsModal";
import useBotActions from "@shared/features/bots/useBotActions";
import useConfirmModal from "common/Modal/useConfirmModal";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import { ItemType } from "antd/es/menu/interface";
import Dropdown from "common/Dropdown/Dropdown";
import { useBottomNavigator, useNavigateToTradesPage } from "utils/hooks/misc";
import Spin from "antd/es/spin";
import Flex from "common/Boxes/Flex";
import { Manual } from "common/Icons/Icons";
import { COPY_TO_WORKSPACE_QUERY_PARAM } from "features/bots/common/constants";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { sharedLib } from "@shared/services/shared.service.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import LinkOutlined from "@ant-design/icons/LinkOutlined";
import DisconnectOutlined from "@ant-design/icons/DisconnectOutlined";
import {
  ENTRY_UUID,
  SIGNAL_ENTRY_UUID,
  SIGNAL_TAKE_PROFIT_UUID,
  TAKE_PROFIT_UUID,
  TRADE_EXTRA_DETAILS_TABS,
} from "@shared/common/constants.ts";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils.ts";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import {
  BOT_ACTION_VIEW_CLOSED_TRADES_ID,
  BOT_ACTIONS_TRIGGER_ID,
} from "testIds.ts";
import { useCanManageBotType } from "@shared/features/bots/hooks.ts";
import LookIcon from "common/Icons/LookIcon.tsx";
import {
  ArrowLeftRight,
  CirclePause,
  CirclePlay,
  Copy,
  History,
  Link2,
  MoreHorizontal,
  MoreVertical,
  Pencil,
  Share2,
  Trash2,
  Unlink,
  Zap,
} from "lucide-react";

interface BotActionsProps {
  bot: BotData | BasicBotData;
  verticalEllipsis?: boolean;
  isCard?: boolean;
}

const BotActions = ({ bot, verticalEllipsis, isCard }: BotActionsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const history = useHistory();
  const appTheme = useTheme();
  const bottomNavigatorData = useBottomNavigator();
  const [getBotUuid, getBotUuidStatus] =
    sharedLib.sharedApi.bots.useLazyGetBotUuidQuery();
  const { isSubUser, isReadOnly } = useSubUserPermissions();

  const navigateToCopiedFromWorkspaceForm = () => {
    const duplicateBotRoute = getDuplicateBotRoute(botType, bot.id);
    const updatedQuery = new URLSearchParams();
    updatedQuery.set(COPY_TO_WORKSPACE_QUERY_PARAM, "true");
    history.push(`${duplicateBotRoute}?${updatedQuery.toString()}`);
  };

  const {
    isInvalidAccount,
    isBotActive,
    botType,
    deleteLoading,
    bulkUpdateBotsModalOpen,
    setBulkUpdateBotsModalOpen,
    setModalIsActiveState,
    deleteBot,
    isSignalsBot,
    isTradingViewSignalsBot,
    setIsEditMode,
    showBotsInactiveAccountMessage,
    handleChangeStatusClick,
    oneClickFollowDefaultData,
    onShareClickError,
    onCopyToWorkspaceClick,
    workspace,
    isTradingViewBot,
    accountIsReadOnly,
    showReadOnlyAccountMessage,
    quotaModal,
  } = useBotActions({
    bot,
    useQuotaModal,
    openMessage,
    displayResultMessage,
    navigateToCopiedFromWorkspaceForm,
  });
  const canManageBotType = useCanManageBotType(botType);
  const { isChannelConfiguration } = useContext(MyBotsPageContext) || {};
  const { openConfirmModal, confirmModalContextHolder } = useConfirmModal({
    loading: deleteLoading,
  });
  const { navigateWithQuery } = useNavigateToTradesPage();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const onDuplicateClick = () => {
    history.push(getDuplicateBotRoute(botType, bot.id));
  };

  const handleDeleteBot = async () => {
    return displayResultMessage(
      deleteBot({ dca_bot_id: bot.id }).unwrap(),
      getBotDeletedMessage(botType, false, t),
      getBotDeleteFailedMessage(botType, false, t)
    ).catch(() => {});
  };

  const onShareClick = async () => {
    if (onShareClickError) {
      openMessage(onShareClickError, "warning");
      return;
    }
    try {
      const urlPromise = getBotUuid({ bot_id: bot.id }).then(
        ({ data: uuid, isError }) => {
          if (!uuid || isError) {
            openMessage(
              t("messages.errors.create_bot_share_link_failed"),
              "error"
            );
            return "";
          } else {
            const path = getPublicBotPath(bot.bot_type, uuid, undefined, true);
            return `${window.location.origin}${path}`;
          }
        }
      );

      await copyCodeToClipboard(urlPromise)
        .then(() => {
          openMessage(t("messages.success.bot_share_link_copied"), "info");
        })
        .catch(() => {
          openMessage(
            t("messages.errors.copy_bot_share_link_failed"),
            "warning"
          );
        });
    } catch {
      return;
    }
  };

  const onViewActiveTradeClick = () => {
    if (bot.active_trade) {
      history.push(
        getTradeExtraDetailsRoute(
          location.pathname,
          bot.active_trade.id,
          TRADE_EXTRA_DETAILS_TABS.TIMELINE
        )
      );
    }
  };

  const onDeleteBotClick = () => {
    if (!canManageBotType) return;
    if (accountIsReadOnly) {
      showReadOnlyAccountMessage();
      return;
    }
    openConfirmModal(
      getBotDeleteWarningMessage(false, t),
      handleDeleteBot,
      true
    );
  };

  const navigationArgs = { [FilterType.SOURCE_NAME]: bot.id };
  const onViewOpenTradesClick = () => {
    navigateWithQuery(OPEN_TRADES_PATH, navigationArgs);
  };
  const onViewClosedTradesClick = () => {
    navigateWithQuery(CLOSED_TRADES_PATH, navigationArgs);
  };

  const onEditClick = () => {
    if (!canManageBotType) return;
    if (accountIsReadOnly) {
      showReadOnlyAccountMessage();
      return;
    }
    if (isInvalidAccount) {
      showBotsInactiveAccountMessage("edit");
    } else {
      history.push(getEditBotRoute(botType, bot.id));
    }
  };

  const closeDropdown = () => setDropdownOpen(false);

  let menuItems: ItemType[] = [];
  if ([BotType.ASAP, BotType.GRID].includes(botType) && bot.active_trade) {
    menuItems.push({
      key: "viewActiveTrade",
      icon: (
        <LookIcon
          classic={<SwapOutlined />}
          newLook={<ArrowLeftRight {...MENU_ICON} />}
        />
      ),
      label: t("actions.view_active_trade"),
      onClick: () => {
        closeDropdown();
        onViewActiveTradeClick();
      },
    });
  }
  if (
    (botType === BotType.TRADING_VIEW || isSignalsBot) &&
    bot.number_of_open_trades
  ) {
    menuItems.push({
      key: "viewOpenTrades",
      icon: (
        <LookIcon
          classic={<SwapOutlined />}
          newLook={<ArrowLeftRight {...MENU_ICON} />}
        />
      ),
      label: t("actions.view_open_trades"),
      onClick: () => {
        closeDropdown();
        onViewOpenTradesClick();
      },
    });
  }
  menuItems.push({
    key: "viewClosedTrades",
    icon: (
      <LookIcon
        classic={<HistoryOutlined />}
        newLook={<History {...MENU_ICON} />}
      />
    ),
    label: (
      <span data-testid={BOT_ACTION_VIEW_CLOSED_TRADES_ID}>
        {t("actions.view_closed_trades")}
      </span>
    ),
    onClick: () => {
      closeDropdown();
      onViewClosedTradesClick();
    },
  });
  if (canManageBotType && botType !== BotType.GRID) {
    menuItems.push({
      key: "editBot",
      icon: (
        <LookIcon
          classic={<EditOutlined />}
          newLook={<Pencil {...MENU_ICON} />}
        />
      ),
      label: t("actions.edit_bot"),
      onClick: () => {
        closeDropdown();
        onEditClick();
      },
      className: isInvalidAccount || accountIsReadOnly ? "invalid" : undefined,
    });
  }
  if (canManageBotType) {
    menuItems = [
      ...menuItems,
      {
        key: "duplicateBot",
        icon: (
          <LookIcon
            classic={<CopyOutlined />}
            newLook={<Copy {...MENU_ICON} />}
          />
        ),
        label: t("actions.duplicate"),
        onClick: () => {
          closeDropdown();
          onDuplicateClick();
        },
      },
      {
        key: "changeBotStatus",
        icon: isBotActive ? (
          <LookIcon
            classic={<PauseCircleOutlined />}
            newLook={<CirclePause {...MENU_ICON} />}
          />
        ) : (
          <LookIcon
            classic={<PlayCircleOutlined />}
            newLook={<CirclePlay {...MENU_ICON} />}
          />
        ),
        label: (
          <Flex>
            {isBotActive
              ? isChannelConfiguration
                ? t("actions.unpublish")
                : t("actions.deactivate")
              : isChannelConfiguration
              ? t("actions.publish")
              : t("actions.activate")}
          </Flex>
        ),
        onClick: () => {
          closeDropdown();
          handleChangeStatusClick().catch(() => {});
        },
        className:
          (!isBotActive && isInvalidAccount) || accountIsReadOnly
            ? "invalid"
            : undefined,
      },
      {
        key: "deleteBot",
        icon: (
          <LookIcon
            classic={<DeleteOutlined />}
            newLook={<Trash2 {...MENU_ICON} />}
          />
        ),
        label: t("buttons.delete"),
        onClick: () => {
          closeDropdown();
          onDeleteBotClick();
        },
        className: accountIsReadOnly ? "invalid" : undefined,
      },
    ];
  }

  if (!isTradingViewSignalsBot && !isReadOnly) {
    menuItems.push({
      key: "shareBot",
      icon: (
        <LookIcon
          classic={<ShareAltOutlined />}
          newLook={<Share2 {...MENU_ICON} />}
        />
      ),
      label: (
        <Flex>
          {t("actions.share")}
          <Spin
            spinning={getBotUuidStatus.isFetching}
            style={{ marginLeft: 10 }}
            size="small"
          />
        </Flex>
      ),
      onClick: () => {
        onShareClick().finally(closeDropdown);
      },
      className:
        botType === BotType.ASAP && !bot.is_public ? "invalid" : undefined,
    });
    if (!isSubUser) {
      menuItems.push({
        key: "copyToWorkspace",
        icon: (
          <LookIcon
            classic={<CopyOutlined />}
            newLook={<Copy {...MENU_ICON} />}
          />
        ),
        label:
          workspace === WorkspaceType.DEMO
            ? t("actions.copy_to_workspace_live")
            : t("actions.copy_to_workspace_demo"),
        onClick: () => {
          closeDropdown();
          onCopyToWorkspaceClick();
        },
      });
    }
  }

  if (isCard && canManageBotType) {
    menuItems = [
      ...menuItems,
      {
        key: "bulkActions",
        icon: (
          <LookIcon
            classic={<ThunderboltOutlined />}
            newLook={<Zap {...MENU_ICON} />}
          />
        ),
        label: t("modals.bulk_actions"),
        onClick: () => {
          closeDropdown();
          setIsEditMode?.(bot);
        },
      },
    ];
  }
  if ((isTradingViewSignalsBot || isTradingViewBot) && !isReadOnly) {
    const entryUuidName = t(
      isTradingViewSignalsBot ? SIGNAL_ENTRY_UUID : ENTRY_UUID
    );
    const takeProfitUuidName = t(
      isTradingViewSignalsBot ? SIGNAL_TAKE_PROFIT_UUID : TAKE_PROFIT_UUID
    );
    menuItems = [
      ...menuItems,
      {
        key: "entryUuid",
        icon: (
          <LookIcon
            classic={<LinkOutlined />}
            newLook={<Link2 {...MENU_ICON} />}
          />
        ),
        label: t("webhooks.copy", { name: entryUuidName }),
        onClick: () => {
          closeDropdown();
          copyCodeToClipboard(bot.entry_uuid || "").catch(() => {});
          openMessage(getCopyMessage(entryUuidName), "info");
        },
      },
      {
        key: "entryTakeProfit",
        icon: (
          <LookIcon
            classic={<DisconnectOutlined />}
            newLook={<Unlink {...MENU_ICON} />}
          />
        ),
        label: t("webhooks.copy", { name: takeProfitUuidName }),
        onClick: () => {
          closeDropdown();
          copyCodeToClipboard(bot.take_profit_uuid || "").catch(() => {});
          openMessage(getCopyMessage(takeProfitUuidName), "info");
        },
      },
    ];
  }
  if (oneClickFollowDefaultData.shouldDisplay) {
    menuItems = [
      ...menuItems,
      { type: "divider" },
      {
        key: oneClickFollowDefaultData.key,
        icon: <Manual />,
        label: (
          <div
            style={{
              maxWidth: bottomNavigatorData.baseShouldDisplay ? "auto" : 200,
            }}
          >
            {oneClickFollowDefaultData.text}
          </div>
        ),
        onClick: () => {
          closeDropdown();
          oneClickFollowDefaultData.onClick().catch(() => {});
        },
      },
    ];
  }

  const onOpenChange = (
    nextOpen: boolean,
    info?: {
      source: "trigger" | "menu";
    }
  ) => {
    if (!info || info.source === "trigger" || nextOpen) {
      setDropdownOpen(nextOpen);
    }
  };

  return (
    <>
      <Dropdown
        title={t("modals.bot_actions")}
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={["click"]}
        onOpenChange={onOpenChange}
        open={dropdownOpen}
        mobileCloseOnSelect={false}
      >
        {appTheme.isNewLook ? (
          <NewLookTrigger data-testid={BOT_ACTIONS_TRIGGER_ID}>
            {verticalEllipsis ? (
              <MoreVertical size={18} strokeWidth={MENU_ICON.strokeWidth} />
            ) : (
              <MoreHorizontal size={18} strokeWidth={MENU_ICON.strokeWidth} />
            )}
          </NewLookTrigger>
        ) : (
          <EllipsisOutlined
            data-testid={BOT_ACTIONS_TRIGGER_ID}
            style={{
              cursor: "pointer",
              fontSize: "30px",
              color: appTheme.colors.basic,
              transform: verticalEllipsis ? "rotate(90deg)" : undefined,
            }}
          />
        )}
      </Dropdown>
      {confirmModalContextHolder}
      <BulkUpdateBotsModal
        closeModal={() => {
          setBulkUpdateBotsModalOpen(false);
          setModalIsActiveState(undefined);
        }}
        botsIDs={[bot.id]}
        open={bulkUpdateBotsModalOpen}
        isDeactivate={isBotActive}
        botType={botType}
        botIDToBotData={{ [bot.id]: bot }}
      />
      {quotaModal}
    </>
  );
};

export default React.memo(BotActions);

const MENU_ICON = { size: 15, strokeWidth: 1.75 };

const NewLookTrigger = styled.span`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${(p) => p.theme.radii.pill}px;
  color: ${(p) => p.theme.colors.secondaryText};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover,
  &.ant-dropdown-open {
    color: ${(p) => p.theme.colors.titleColor};
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
`;
